import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import axios from 'axios'

// ** Async Thunks
export const processPayment = createAsyncThunk(
  'payments/processPayment',
  async (paymentData, { rejectWithValue }) => {
    try {
      const response = await axios.post('/payments/process', paymentData)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Payment processing failed')
    }
  }
)

export const validatePayment = createAsyncThunk(
  'payments/validatePayment',
  async (paymentData, { rejectWithValue }) => {
    try {
      const response = await axios.post('/payments/validate', paymentData)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Payment validation failed')
    }
  }
)

export const getPaymentMethods = createAsyncThunk(
  'payments/getPaymentMethods',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`/payments/methods/${userId}`)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch payment methods')
    }
  }
)

export const savePaymentMethod = createAsyncThunk(
  'payments/savePaymentMethod',
  async ({ userId, paymentMethod }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`/payments/methods/${userId}`, paymentMethod)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to save payment method')
    }
  }
)

export const deletePaymentMethod = createAsyncThunk(
  'payments/deletePaymentMethod',
  async ({ userId, methodId }, { rejectWithValue }) => {
    try {
      await axios.delete(`/payments/methods/${userId}/${methodId}`)
      return methodId
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete payment method')
    }
  }
)

export const getPaymentHistory = createAsyncThunk(
  'payments/getPaymentHistory',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await axios.get('/payments/history', { params })
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch payment history')
    }
  }
)

// ** Initial State
const initialState = {
  // Current payment session
  currentPayment: {
    amount: 0,
    currency: 'USD',
    description: '',
    paymentMethod: null,
    customerInfo: null,
    metadata: {},
    status: 'pending', // 'pending', 'processing', 'completed', 'failed', 'cancelled'
    transactionId: null,
    gatewayResponse: null,
    errors: []
  },
  
  // Payment methods
  paymentMethods: [],
  selectedPaymentMethod: null,
  
  // Payment history
  paymentHistory: [],
  
  // OTP verification
  otpVerification: {
    isRequired: false,
    isVerified: false,
    phoneNumber: '',
    attempts: 0,
    maxAttempts: 3,
    expiresAt: null
  },
  
  // Gift card handling
  giftCard: {
    isAvailable: false,
    data: null,
    processing: false,
    errors: []
  },
  
  // Watchdog decline handling
  watchdogDecline: {
    isAvailable: false,
    sessionData: null,
    processing: false,
    errors: []
  },
  
  // Form states
  forms: {
    paymentForm: {
      isDirty: false,
      hasErrors: false,
      currentStep: 1,
      totalSteps: 3,
      validationErrors: {}
    },
    otpForm: {
      isDirty: false,
      hasErrors: false,
      validationErrors: {}
    }
  },
  
  // Loading states
  loading: {
    processing: false,
    validating: false,
    fetchingMethods: false,
    savingMethod: false,
    deletingMethod: false,
    fetchingHistory: false
  },
  
  // Error states
  error: null,
  
  // Success states
  success: null,
  
  // Statistics
  statistics: {
    totalProcessed: 0,
    totalAmount: 0,
    successRate: 0,
    averageAmount: 0,
    todayStats: {
      count: 0,
      amount: 0
    }
  }
}

// ** Slice
const paymentSlice = createSlice({
  name: 'payments',
  initialState,
  reducers: {
    // Payment form actions
    setPaymentAmount: (state, action) => {
      state.currentPayment.amount = action.payload
    },
    
    setPaymentCurrency: (state, action) => {
      state.currentPayment.currency = action.payload
    },
    
    setPaymentDescription: (state, action) => {
      state.currentPayment.description = action.payload
    },
    
    setPaymentMethod: (state, action) => {
      state.currentPayment.paymentMethod = action.payload
      state.selectedPaymentMethod = action.payload
    },
    
    setCustomerInfo: (state, action) => {
      state.currentPayment.customerInfo = action.payload
    },
    
    setPaymentMetadata: (state, action) => {
      state.currentPayment.metadata = { ...state.currentPayment.metadata, ...action.payload }
    },
    
    setPaymentStatus: (state, action) => {
      state.currentPayment.status = action.payload
    },
    
    setGatewayResponse: (state, action) => {
      state.currentPayment.gatewayResponse = action.payload
    },
    
    addPaymentError: (state, action) => {
      state.currentPayment.errors.push(action.payload)
    },
    
    clearPaymentErrors: (state) => {
      state.currentPayment.errors = []
    },
    
    // Payment methods actions
    setSelectedPaymentMethod: (state, action) => {
      state.selectedPaymentMethod = action.payload
    },
    
    // OTP verification actions
    setOTPRequired: (state, action) => {
      state.otpVerification.isRequired = action.payload
    },
    
    setOTPVerified: (state, action) => {
      state.otpVerification.isVerified = action.payload
    },
    
    setOTPPhoneNumber: (state, action) => {
      state.otpVerification.phoneNumber = action.payload
    },
    
    incrementOTPAttempts: (state) => {
      state.otpVerification.attempts += 1
    },
    
    resetOTPAttempts: (state) => {
      state.otpVerification.attempts = 0
    },
    
    setOTPExpiry: (state, action) => {
      state.otpVerification.expiresAt = action.payload
    },
    
    // Gift card actions
    setGiftCardAvailable: (state, action) => {
      state.giftCard.isAvailable = action.payload
    },
    
    setGiftCardData: (state, action) => {
      state.giftCard.data = action.payload
    },
    
    setGiftCardProcessing: (state, action) => {
      state.giftCard.processing = action.payload
    },
    
    addGiftCardError: (state, action) => {
      state.giftCard.errors.push(action.payload)
    },
    
    clearGiftCardErrors: (state) => {
      state.giftCard.errors = []
    },
    
    // Watchdog decline actions
    setWatchdogDeclineAvailable: (state, action) => {
      state.watchdogDecline.isAvailable = action.payload
    },
    
    setWatchdogDeclineSessionData: (state, action) => {
      state.watchdogDecline.sessionData = action.payload
    },
    
    setWatchdogDeclineProcessing: (state, action) => {
      state.watchdogDecline.processing = action.payload
    },
    
    addWatchdogDeclineError: (state, action) => {
      state.watchdogDecline.errors.push(action.payload)
    },
    
    clearWatchdogDeclineErrors: (state) => {
      state.watchdogDecline.errors = []
    },
    
    // Form actions
    setFormState: (state, action) => {
      const { formName, updates } = action.payload
      state.forms[formName] = { ...state.forms[formName], ...updates }
    },
    
    setFormValidationErrors: (state, action) => {
      const { formName, errors } = action.payload
      state.forms[formName].validationErrors = errors
    },
    
    resetFormState: (state, action) => {
      const { formName } = action.payload
      state.forms[formName] = initialState.forms[formName]
    },
    
    // Error and success actions
    setError: (state, action) => {
      state.error = action.payload
    },
    
    clearError: (state) => {
      state.error = null
    },
    
    setSuccess: (state, action) => {
      state.success = action.payload
    },
    
    clearSuccess: (state) => {
      state.success = null
    },
    
    // Reset actions
    resetPaymentState: () => initialState,
    resetCurrentPayment: (state) => {
      state.currentPayment = initialState.currentPayment
    },
    resetOTPVerification: (state) => {
      state.otpVerification = initialState.otpVerification
    },
    resetGiftCard: (state) => {
      state.giftCard = initialState.giftCard
    },
    resetWatchdogDecline: (state) => {
      state.watchdogDecline = initialState.watchdogDecline
    }
  },
  extraReducers: (builder) => {
    builder
      // Process Payment
      .addCase(processPayment.pending, (state) => {
        state.loading.processing = true
        state.currentPayment.status = 'processing'
        state.error = null
      })
      .addCase(processPayment.fulfilled, (state, action) => {
        state.loading.processing = false
        state.currentPayment.status = 'completed'
        state.currentPayment.transactionId = action.payload.transactionId
        state.currentPayment.gatewayResponse = action.payload
        state.success = 'Payment processed successfully'
        
        // Update statistics
        state.statistics.totalProcessed += 1
        state.statistics.totalAmount += action.payload.amount
        state.statistics.todayStats.count += 1
        state.statistics.todayStats.amount += action.payload.amount
      })
      .addCase(processPayment.rejected, (state, action) => {
        state.loading.processing = false
        state.currentPayment.status = 'failed'
        state.error = action.payload
        state.currentPayment.errors.push(action.payload)
      })
      
      // Validate Payment
      .addCase(validatePayment.pending, (state) => {
        state.loading.validating = true
        state.error = null
      })
      .addCase(validatePayment.fulfilled, (state, action) => {
        state.loading.validating = false
        state.currentPayment.metadata = { ...state.currentPayment.metadata, ...action.payload }
      })
      .addCase(validatePayment.rejected, (state, action) => {
        state.loading.validating = false
        state.error = action.payload
      })
      
      // Get Payment Methods
      .addCase(getPaymentMethods.pending, (state) => {
        state.loading.fetchingMethods = true
        state.error = null
      })
      .addCase(getPaymentMethods.fulfilled, (state, action) => {
        state.loading.fetchingMethods = false
        state.paymentMethods = action.payload
      })
      .addCase(getPaymentMethods.rejected, (state, action) => {
        state.loading.fetchingMethods = false
        state.error = action.payload
      })
      
      // Save Payment Method
      .addCase(savePaymentMethod.pending, (state) => {
        state.loading.savingMethod = true
        state.error = null
      })
      .addCase(savePaymentMethod.fulfilled, (state, action) => {
        state.loading.savingMethod = false
        state.paymentMethods.push(action.payload)
        state.success = 'Payment method saved successfully'
      })
      .addCase(savePaymentMethod.rejected, (state, action) => {
        state.loading.savingMethod = false
        state.error = action.payload
      })
      
      // Delete Payment Method
      .addCase(deletePaymentMethod.pending, (state) => {
        state.loading.deletingMethod = true
        state.error = null
      })
      .addCase(deletePaymentMethod.fulfilled, (state, action) => {
        state.loading.deletingMethod = false
        state.paymentMethods = state.paymentMethods.filter(method => method.id !== action.payload)
        state.success = 'Payment method deleted successfully'
      })
      .addCase(deletePaymentMethod.rejected, (state, action) => {
        state.loading.deletingMethod = false
        state.error = action.payload
      })
      
      // Get Payment History
      .addCase(getPaymentHistory.pending, (state) => {
        state.loading.fetchingHistory = true
        state.error = null
      })
      .addCase(getPaymentHistory.fulfilled, (state, action) => {
        state.loading.fetchingHistory = false
        state.paymentHistory = action.payload.data || action.payload
      })
      .addCase(getPaymentHistory.rejected, (state, action) => {
        state.loading.fetchingHistory = false
        state.error = action.payload
      })
  }
})

export const {
  // Payment form actions
  setPaymentAmount,
  setPaymentCurrency,
  setPaymentDescription,
  setPaymentMethod,
  setCustomerInfo,
  setPaymentMetadata,
  setPaymentStatus,
  setGatewayResponse,
  addPaymentError,
  clearPaymentErrors,
  
  // Payment methods actions
  setSelectedPaymentMethod,
  
  // OTP verification actions
  setOTPRequired,
  setOTPVerified,
  setOTPPhoneNumber,
  incrementOTPAttempts,
  resetOTPAttempts,
  setOTPExpiry,
  
  // Gift card actions
  setGiftCardAvailable,
  setGiftCardData,
  setGiftCardProcessing,
  addGiftCardError,
  clearGiftCardErrors,
  
  // Watchdog decline actions
  setWatchdogDeclineAvailable,
  setWatchdogDeclineSessionData,
  setWatchdogDeclineProcessing,
  addWatchdogDeclineError,
  clearWatchdogDeclineErrors,
  
  // Form actions
  setFormState,
  setFormValidationErrors,
  resetFormState,
  
  // Error and success actions
  setError,
  clearError,
  setSuccess,
  clearSuccess,
  
  // Reset actions
  resetPaymentState,
  resetCurrentPayment,
  resetOTPVerification,
  resetGiftCard,
  resetWatchdogDecline
} = paymentSlice.actions

export default paymentSlice.reducer
